import { getApiUrl } from '../config/api';
import { authedFetch } from '../utils/authedFetch';
import { QueueLineReply } from '../utils/queueMove';

export interface QueueTicket {
  id: number;
  brand_id: number;
  tend_content_id: number;
  platform: string;
  position: number;
  queued_at: string | null;
  /** Edge flags for this item's channel line (drive the move button) */
  is_front?: boolean;
  is_back?: boolean;
  /** Scheduler lifecycle: queued | scheduled | publishing | failed | held */
  status?: string;
  scheduled_at?: string | null;
  /** Display-only projection of when this queued ticket should fire (walks
      cadence windows forward; UTC iso). Null = beyond horizon or line gated. */
  projected_at?: string | null;
  /** Why the line can't fire when it can't: ok | paused | manual_only | auto_off | not_connected | cooling_off (a spam verdict paused it) | relink_hold (the account was just (re)linked) — line_next_open_at says until when */
  line_reason?: string | null;
  line_open_now?: boolean;
  /** When the line's next cadence window opens (UTC iso) — set when closed now */
  line_next_open_at?: string | null;
  attempts?: number;
  last_error?: string | null;
  /** Why the SYSTEM holds it when status='held': over_limit | not_connected | paced (a clock — resumes on its own at scheduled_at). Null = a person's hold. */
  hold_reason?: string | null;
  /** 1-based ordinal place in the line (positions are sparse — never show raw position) */
  rank?: number | null;
  /** Enqueue reply: the item carried a take request and queueing cleared
      it (Take precedes Queued — the take is done, recorded or waived). */
  take_cleared?: boolean;
}

export interface QueueItemStatus {
  queued: boolean;
  position?: number;
  platform?: string;
  is_front?: boolean;
  is_back?: boolean;
  /** Scheduler lifecycle: queued | scheduled | publishing | failed | held */
  status?: string;
  /** Set when status='scheduled' — UTC iso; render browser-local */
  scheduled_at?: string | null;
  /** Display-only projection of when this queued ticket should fire (walks
      cadence windows forward; UTC iso). Null = beyond horizon or line gated. */
  projected_at?: string | null;
  /** Why the line can't fire when it can't: ok | paused | manual_only | auto_off | not_connected | cooling_off (a spam verdict paused it) | relink_hold (the account was just (re)linked) — line_next_open_at says until when */
  line_reason?: string | null;
  line_open_now?: boolean;
  /** When the line's next cadence window opens (UTC iso) — set when closed now */
  line_next_open_at?: string | null;
  last_error?: string | null;
  /** Failure classification when status='failed': auth / not_linked / account
      (fix = the channel) | too_long (fix = edit) | rejected (the network said
      no to this post — won't retry) | rate_limited / transient (was retrying)
      | other (older rows, text-classified) */
  error_kind?: 'auth' | 'not_linked' | 'account' | 'too_long' | 'rejected' | 'unknown' | 'rate_limited' | 'transient' | 'other' | null;
  /** Why the SYSTEM holds it when status='held': over_limit (edit to resume) | not_connected | paced (resumes on its own at scheduled_at). Null = a person's hold. */
  hold_reason?: 'over_limit' | 'not_connected' | string | null;
  /** 1-based ordinal place in the line (positions are sparse — never show raw position) */
  rank?: number | null;
  /** Client-side only, on a `queued: false` push: WHY the item left its
      line. 'take' = a take was requested on it (Take precedes Queued) —
      the channel strip keeps the card in place until refresh instead of
      dropping it like a plain dequeue. */
  left_for?: 'take' | null;
}

const authHeaders = (): Record<string, string> => ({
  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

/** Queue a tendv2 item to the back or front of its channel's line. */
export async function enqueueContent(
  tendContentId: number | string,
  brandId: number,
  placement: 'back' | 'front' = 'back'
): Promise<QueueTicket> {
  const id = typeof tendContentId === 'string' ? parseInt(tendContentId, 10) : tendContentId;
  const response = await authedFetch(
    getApiUrl(`/tend/queue/${id}?brand_id=${brandId}&placement=${placement}`),
    { method: 'POST', headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to queue content' }));
    throw new Error(error.detail || 'Failed to queue content');
  }
  return response.json();
}

/** Remove a tendv2 item's ticket — back to plain tended. */
export async function dequeueContent(tendContentId: number | string): Promise<void> {
  const id = typeof tendContentId === 'string' ? parseInt(tendContentId, 10) : tendContentId;
  const response = await authedFetch(
    getApiUrl(`/tend/queue/${id}`),
    { method: 'DELETE', headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to remove from queue' }));
    throw new Error(error.detail || 'Failed to remove from queue');
  }
}

/** Pause a ticket in place — keeps its position, the queue flows around it. */
export async function holdContent(tendContentId: number | string): Promise<QueueTicket> {
  const id = typeof tendContentId === 'string' ? parseInt(tendContentId, 10) : tendContentId;
  const response = await authedFetch(
    getApiUrl(`/tend/queue/${id}/hold`),
    { method: 'POST', headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to hold' }));
    throw new Error(error.detail || 'Failed to hold');
  }
  return response.json();
}

/** Return a held ticket to the flow in its place (held -> queued). */
export async function resumeContent(tendContentId: number | string): Promise<QueueTicket> {
  const id = typeof tendContentId === 'string' ? parseInt(tendContentId, 10) : tendContentId;
  const response = await authedFetch(
    getApiUrl(`/tend/queue/${id}/resume`),
    { method: 'POST', headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to resume' }));
    throw new Error(error.detail || 'Failed to resume');
  }
  return response.json();
}

/** The endpoint rejects >200 ids; the Tended LIST mounts every row's
 * button in one batch window (500+ on big brands), so chunk here — every
 * caller stays safe. A failed chunk throws (the status contexts treat
 * total failure conservatively: stale cache, never "not queued"). */
const QUEUE_STATUS_CHUNK = 150;

/** Bulk queued-state lookup: {id: {queued, position}}. Missing id = not queued. */
export async function getQueueStatuses(
  ids: number[]
): Promise<Record<string, QueueItemStatus>> {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += QUEUE_STATUS_CHUNK) {
    chunks.push(ids.slice(i, i + QUEUE_STATUS_CHUNK));
  }
  const maps = await Promise.all(chunks.map(async (chunk) => {
    const response = await authedFetch(
      getApiUrl(`/tend/queue/items?ids=${chunk.join(',')}`),
      { headers: authHeaders() }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get queue statuses' }));
      throw new Error(error.detail || 'Failed to get queue statuses');
    }
    const data = await response.json();
    return (data.items || {}) as Record<string, QueueItemStatus>;
  }));
  return Object.assign({}, ...maps);
}

/** One page of hydrated tendv2 items for a brand+channel feed strip.
    state 'q' = queued (front of line first, items carry queue_position);
    state 't' = tended/unqueued (newest first);
    state 'p' = published by this brand (newest publish first, items carry
    publish_history_id/post_url/publish_status and a brand-attributed
    published_at). platform is required for q/t; for p it may be omitted
    (or 'all') for the cross-channel merged feed. */
export async function listBrandTendv2Items(
  slug: string,
  platform: string | undefined,
  state: 'q' | 't' | 'p',
  page: number = 1,
  pageSize: number = 6,
  /** Take phase 1: take = only this brand's take requests (state=t = the
      Take queue: requested, not yet queued); order = by request time,
      newest first unless 'oldest'. */
  opts: { take?: boolean; order?: 'newest' | 'oldest' } = {}
): Promise<{ items: any[]; total: number }> {
  const params = new URLSearchParams({
    state, page: String(page), page_size: String(pageSize),
  });
  if (platform) params.set('platform', platform);
  if (opts.take) params.set('take', 'true');
  if (opts.take && opts.order) params.set('order', opts.order);
  const response = await authedFetch(
    getApiUrl(`/tend/brands/${encodeURIComponent(slug)}/tendv2-items?${params}`),
    { headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to list items' }));
    throw new Error(error.detail || 'Failed to list items');
  }
  const data = await response.json();
  return { items: data.items || [], total: data.total || 0 };
}

/** The server refused a move because the line changed under us (the
    moved card or its target left the line, or someone moved the card
    since the strip read it). The strip reloads and says so. */
export class QueueConflictError extends Error {
  readonly conflict = true;
  constructor(message: string) {
    super(message);
    this.name = 'QueueConflictError';
  }
}

/** Move one card within its channel line (drag-drop): put movedId before
    anchor.beforeId, or after anchor.afterId, or at the back (neither),
    applied to the line as it is NOW. expectedPosition = the moved card's
    position as the strip last saw it; a different one on the server is a
    conflict (409 → QueueConflictError). Resolves to the whole line front
    to back — set the strip from it (utils/queueMove.applyLineReply).
    Today's fire times follow the new order server-side: the card dropped
    at #1 takes today's first time. */
export async function moveQueueTicket(
  slug: string,
  platform: string,
  movedId: number,
  anchor: { beforeId?: number | null; afterId?: number | null },
  expectedPosition: number | null,
): Promise<QueueLineReply> {
  const response = await authedFetch(
    getApiUrl(`/tend/queue/brands/${encodeURIComponent(slug)}/reorder`),
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        platform,
        moved_id: movedId,
        before_id: anchor.beforeId ?? null,
        after_id: anchor.afterId ?? null,
        expected_position: expectedPosition,
      }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to move the card' }));
    const message = error.detail || 'Failed to move the card';
    if (response.status === 409) throw new QueueConflictError(message);
    throw new Error(message);
  }
  const data = await response.json();
  return { order: data.order || [], tickets: data.tickets || {} };
}

/** Ordered per-channel queues for a brand (front of line first). */
export async function listBrandQueue(
  slug: string,
  platform?: string
): Promise<Record<string, QueueTicket[]>> {
  const params = platform ? `?platform=${encodeURIComponent(platform)}` : '';
  const response = await authedFetch(
    getApiUrl(`/tend/queue/brands/${encodeURIComponent(slug)}${params}`),
    { headers: authHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to list queue' }));
    throw new Error(error.detail || 'Failed to list queue');
  }
  const data = await response.json();
  return data.queues || {};
}
